// SVG-лінії схеми: головна шина 10 кВ і розводка до вузлів.
//
// ГОЛОВНЕ РІШЕННЯ: координати X у ВІДСОТКАХ прямо в атрибутах <line>, без
// viewBox. Попередня реалізація рахувала геометрію через getBoundingClientRect()
// кожної картки плюс ResizeObserver — працювало, але означало перерахунок
// layout на кожен ресайз і перемикання вкладки. Тут КТП стоять у CSS-сітці з
// рівними колонками, тож центр i-ї колонки завжди (i + 0.5) / n: лінії лягають
// самі, нічого міряти не треба.
//
// ЧОМУ НЕ viewBox + preserveAspectRatio="none". Спокуслива ідея взяти viewBox
// "0 0 100 h" і працювати в одиницях 0..100 дає неоднорідний масштаб: по X
// розтяг у десятки разів, по Y — одиниця. Через це кола вузлів стають
// еліпсами, а штрихи пунктиру розповзаються. vectorEffect рятує лише товщину
// обведення, але не геометрію. Відсотки в <line> масштабу не мають узагалі,
// тому працюють точно.
//
// Анімація потоку — чистий CSS (stroke-dashoffset), без бібліотек.
import { scada, stateColor } from '@/scada/tokens';
import type { SchemeState } from './types';

/** Центр i-ї колонки з n у відсотках ширини. */
export const colCenter = (i: number, n: number) => `${((i + 0.5) / n) * 100}%`;

const FLOW_CSS = '@keyframes schemeFlow { to { stroke-dashoffset: -22; } }';

interface Segment {
  x1: string | number;
  y1: number;
  x2: string | number;
  y2: number;
  state: SchemeState;
  /** Підсвічена гілка — вибрана користувачем. */
  active?: boolean;
}

function Segments({ segments }: { segments: Segment[] }) {
  return (
    <>
      {segments.map((s, i) => {
        const c = stateColor(s.state);
        // Непрацююче обладнання не «тече»: лінія без біжучого пунктиру —
        // інакше схема брехала б, показуючи потік там, де його немає.
        const flowing = s.state !== 'offline' && s.state !== 'alarm';
        return (
          <g key={i}>
            <line
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke={c}
              strokeWidth={s.active ? 2.4 : 1.6}
              strokeLinecap="round"
              style={{ transition: 'stroke-width .15s ease' }}
            />
            {flowing && (
              <line
                x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                stroke="#fff"
                strokeOpacity={s.active ? 0.75 : 0.4}
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeDasharray="3 12"
                style={{ animation: `schemeFlow ${s.active ? 0.8 : 1.6}s linear infinite` }}
              />
            )}
          </g>
        );
      })}
    </>
  );
}

/** Найгірший стан у наборі: шина здорова рівно настільки, наскільки її секції. */
function worst(states: SchemeState[]): SchemeState {
  if (states.some((s) => s === 'alarm')) return 'alarm';
  if (states.some((s) => s === 'warning')) return 'warning';
  if (states.length > 0 && states.every((s) => s === 'offline')) return 'offline';
  return 'ok';
}

/**
 * Шина 10 кВ: вертикаль від точки приєднання вниз, горизонталь уздовж усіх
 * КТП і вертикальні відводи в кожну колонку.
 */
export function BusBand({ count, states, activeIndex, height = 58 }: {
  count: number;
  states: SchemeState[];
  activeIndex: number | null;
  height?: number;
}) {
  if (count === 0) return null;
  const busY = Math.round(height * 0.45);
  const busState = worst(states);

  const segments: Segment[] = [
    // Ввід з точки приєднання — вона стоїть по центру над схемою.
    { x1: '50%', y1: 0, x2: '50%', y2: busY, state: busState },
    { x1: colCenter(0, count), y1: busY, x2: colCenter(count - 1, count), y2: busY, state: busState },
    ...states.map((st, i) => ({
      x1: colCenter(i, count), y1: busY,
      x2: colCenter(i, count), y2: height,
      state: st,
      active: activeIndex === i,
    })),
  ];

  return (
    <svg width="100%" height={height} style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
      <style>{FLOW_CSS}</style>
      <Segments segments={segments} />
      {/* Вузли приєднання КТП до шини — крапки на перетині. */}
      {states.map((st, i) => (
        <circle key={i} cx={colCenter(i, count)} cy={busY} r={3.5}
          fill={stateColor(st)} stroke={scada.panel} strokeWidth={1.5} />
      ))}
    </svg>
  );
}

/**
 * Розводка від однієї КТП до її груп: вертикаль вниз, розгалуження навпіл,
 * вертикалі в центри груп. Координати — частки ширини ОДНІЄЇ колонки, тож
 * компонент не залежить від кількості КТП на схемі.
 */
export function KtpToGroupsBand({ groupStates, active, height = 26 }: {
  groupStates: SchemeState[];
  active: boolean;
  height?: number;
}) {
  const n = groupStates.length;
  if (n === 0) return null;
  const midY = Math.round(height * 0.5);
  const parent = worst(groupStates);

  const segments: Segment[] = [
    { x1: '50%', y1: 0, x2: '50%', y2: midY, state: parent, active },
    { x1: colCenter(0, n), y1: midY, x2: colCenter(n - 1, n), y2: midY, state: parent, active },
    ...groupStates.map((st, i) => ({
      x1: colCenter(i, n), y1: midY,
      x2: colCenter(i, n), y2: height,
      state: st,
      active,
    })),
  ];

  return (
    <svg width="100%" height={height} style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
      <style>{FLOW_CSS}</style>
      <Segments segments={segments} />
    </svg>
  );
}
